import { SessionState } from './SendabilityPolicy';
import Constants from '../../analytics/constants';
import CoreConstants from '../../core/utils/CoreConstants';
import Log from '../../common/log';
import { SessionsTag } from '../../common/Constants';
/**
 * Core request processing orchestrator
 */
export default class RequestProcessor {
    constructor(core, queue, sendabilityPolicy, retryPolicy, flushController, expirationRecovery) {
        this.processing = false;
        this.processTimeout = null;
        this.startRequest = null;
        // Listener hooks
        this.onWillSendListeners = [];
        this.onRequestSentListeners = [];
        this.core = core;
        this.queue = queue;
        this.sendabilityPolicy = sendabilityPolicy;
        this.retryPolicy = retryPolicy;
        this.flushController = flushController;
        this.expirationRecovery = expirationRecovery;
    }
    /**
     * Add listener hooks
     */
    setListeners(onWillSend, onRequestSent) {
        this.onWillSendListeners = onWillSend;
        this.onRequestSentListeners = onRequestSent;
    }
    /**
     * Process requests in the queue
     */
    processRequests() {
        this.clearProcessingTimeout();
        if (this.queue.isEmpty()) {
            // No requests to process - complete flush if active
            this.flushController.complete();
            return;
        }
        if (this.processing) {
            // Already processing requests
            return;
        }
        if (this.core.getFastDataSessionToken() === '') {
            // No session token available: wait and retry
            this.delayProcessing(1000);
            return;
        }
        if (this.expirationRecovery.hasExpired()) {
            this.processing = true;
            if (!this.core.isSessionRecoveryEnabled()) {
                // Recovery disabled by host app: drop queued events, fire SESSION_EXPIRE,
                // and stay idle until the app starts a new session itself.
                Log.notice(SessionsTag, 'Session expired; recovery disabled — dropping queued requests');
                this.expirationRecovery.expireWithoutRecovery();
                this.queue.clear();
                this.sendabilityPolicy.setState(SessionState.STOPPED);
                this.startRequest = null;
                this.processing = false;
                this.flushController.complete();
                return;
            }
            // Recover expired session
            const startRequest = this.expirationRecovery.recover(this.startRequest);
            this.queue.unshift(startRequest);
            this.sendabilityPolicy.setState(SessionState.STOPPED);
            this.startRequest = null;
            this.processing = false;
            // Process again after delay
            this.delayProcessing(1000);
            return;
        }
        // Lock queue and send first request
        this.processing = true;
        const request = this.queue.peek();
        if (!request) {
            this.processing = false;
            return;
        }
        const sendabilityResult = this.sendabilityPolicy.canSend(request);
        if (sendabilityResult.sendable) {
            this.prepareAndSendRequest(request);
        }
        else {
            this.handleUnsendableRequest(request);
        }
    }
    /**
     * Prepare request params and send it
     */
    prepareAndSendRequest(request) {
        // Prepare params
        this.prepareRequestParams(request);
        // Notify willSend listeners
        for (const listener of this.onWillSendListeners) {
            listener(request);
        }
        // Log request
        Log.notice(SessionsTag, this.getRequestDescription(request));
        // Send request
        this.core.pushData(this.core.getOptions().offline ? CoreConstants.Consumer.BLACK_HOLE : CoreConstants.Consumer.NQS, request.getService(), request.getMethod(), request.getParams(), () => this.onRequestSuccess(request), () => this.onRequestFailure(request));
    }
    /**
     * Handle successful request
     */
    onRequestSuccess(request) {
        var _a;
        // Update expiration manager
        this.expirationRecovery.getExpirationManager().onRequestSent();
        // Handle service-specific logic
        const service = request.getService();
        if (service === Constants.Service.SESSION_START) {
            this.sendabilityPolicy.setState(SessionState.STARTED);
            this.startRequest = request;
        }
        else if (service === Constants.Service.SESSION_STOP) {
            this.sendabilityPolicy.setState(SessionState.STOPPED);
            this.expirationRecovery.closeSession();
            this.startRequest = null;
        }
        // Notify sent listeners
        for (const listener of this.onRequestSentListeners) {
            listener(request);
        }
        // Reset retry counter
        this.retryPolicy.reset();
        // Call request callback
        (_a = request.getOnSuccess()) === null || _a === void 0 ? void 0 : _a();
        // Remove request from queue
        this.queue.dequeue();
        // Unlock and process next
        this.processing = false;
        this.processRequests();
    }
    /**
     * Handle failed request
     */
    onRequestFailure(request) {
        var _a;
        const retryDecision = this.retryPolicy.shouldRetry(this.getRequestDescription(request));
        if (retryDecision.shouldRetry) {
            // Retry request
            this.processing = false;
            this.delayProcessing(retryDecision.delay);
        }
        else {
            // Max retries reached - discard request
            if (this.flushController.isFlushing()) {
                this.flushController.recordFailedRequest(request);
            }
            this.retryPolicy.reset();
            // Call request callback
            (_a = request.getOnFail()) === null || _a === void 0 ? void 0 : _a();
            // Remove request from queue
            this.queue.dequeue();
            // Unlock and process next
            this.processing = false;
            this.processRequests();
        }
    }
    /**
     * Handle request that cannot be sent
     */
    handleUnsendableRequest(request) {
        const service = request.getService();
        let callback;
        // Determine which callback to call based on current state
        switch (service) {
            case Constants.Service.SESSION_START:
                callback = this.sendabilityPolicy.isStarted() ? request.getOnSuccess() : request.getOnFail();
                break;
            case Constants.Service.SESSION_STOP:
                callback = this.sendabilityPolicy.isStopped() ? request.getOnSuccess() : request.getOnFail();
                break;
            default:
                callback = request.getOnFail();
                break;
        }
        // Call callback
        callback === null || callback === void 0 ? void 0 : callback();
        // Remove request from queue
        this.queue.dequeue();
        // Unlock and process next
        this.processing = false;
        this.processRequests();
    }
    /**
     * Prepare request parameters (inject sessionId and beatTime)
     */
    prepareRequestParams(request) {
        const service = request.getService();
        request.setParam('sessionId', request.getParam('sessionId') || this.core.getFastDataSessionToken());
        if (service === Constants.Service.SESSION_START) {
            request.setParam('beatTime', request.getParam('beatTime') || this.core.getFastDataService().getBeatTime());
        }
    }
    /**
     * Get request description for logging
     */
    getRequestDescription(request) {
        let description = '';
        const service = request.getService();
        switch (service) {
            case Constants.Service.SESSION_START:
            case Constants.Service.SESSION_STOP:
            case Constants.Service.NAV:
            case Constants.Service.SESSION_ERROR:
                description = request.getParams().route || '';
                break;
            case Constants.Service.EVENT:
            case Constants.Service.EVENT_END:
                description = request.getParams().name || '';
                break;
        }
        return `[${this.core.getFastDataSessionToken()}] ${service} ${description}`;
    }
    /**
     * Delay request processing
     */
    delayProcessing(delayMs) {
        this.clearProcessingTimeout();
        this.processTimeout = setTimeout(() => {
            this.processTimeout = null;
            this.processRequests();
        }, delayMs);
    }
    /**
     * Clear processing timeout
     */
    clearProcessingTimeout() {
        if (this.processTimeout) {
            clearTimeout(this.processTimeout);
            this.processTimeout = null;
        }
    }
    /**
     * Reset in-flight processor state without tearing it down.
     * Used by destroySession() so the host can immediately start a fresh session.
     */
    resetState() {
        this.clearProcessingTimeout();
        this.processing = false;
        this.startRequest = null;
    }
    /**
     * Destroy processor (cleanup)
     */
    destroy() {
        this.clearProcessingTimeout();
        this.processing = false;
        this.startRequest = null;
    }
}
